import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  Typography,
  Divider
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import InventoryIcon from '@mui/icons-material/Inventory';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import type { InventoryItem } from '../types/inventory.types';

interface SingleItemDeleteDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  item: InventoryItem | null;
  deleting: boolean;
}

export const SingleItemDeleteDialog: React.FC<SingleItemDeleteDialogProps> = ({
  open,
  onClose,
  onConfirm,
  item,
  deleting,
}) => {
  // Para birimi sembolü
  const getCurrencySymbol = (currency?: string): string => {
    switch (currency) {
      case 'USD': return '$';
      case 'EUR': return '€';
      case 'GBP': return '£';
      case 'TRY':
      default: return '₺';
    }
  };

  return (
    <Dialog
      open={open}
      onClose={!deleting ? onClose : undefined}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <DeleteIcon color="error" />
        Stok Ürününü Sil
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          {item ? (
            <>
              <b>{item.name}</b> adlı stok ürününü silmek istediğinize emin misiniz? 
            </>
          ) : (
            'Bu stok ürününü silmek istediğinize emin misiniz?'
          )}
        </DialogContentText>

        {item && (
          <Box sx={{ 
            mt: 2, 
            p: 2, 
            bgcolor: 'grey.50', 
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'grey.300'
          }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Ürün Bilgileri
            </Typography>
            <Divider sx={{ mb: 1.5 }} />
            
            {/* Stok Bilgisi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <InventoryIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.primary">
                <strong>Stok:</strong> {item.warehouseStocks?.reduce((sum, ws) => sum + ws.quantity, 0) || 0} {item.unit || 'adet'}
              </Typography>
            </Box>

            {/* Fiyat Bilgisi */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <MonetizationOnIcon color="action" fontSize="small" />
              <Typography variant="body2" color="text.primary">
                <strong>Satış Fiyatı:</strong> {item.salePrice.toFixed(2)} {getCurrencySymbol(item.saleCurrency)}
              </Typography>
            </Box>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          color="primary"
          disabled={deleting}
        >
          İptal
        </Button>
        <Button 
          onClick={onConfirm} 
          color="error" 
          variant="contained"
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={20} color="inherit" /> : <DeleteIcon />}
        >
          {deleting ? 'Siliniyor...' : 'Sil'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

